<script setup>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import {
  fetchUserById,
  fetchUserPurchases,
  fetchUserWallet,
  fetchUserSessions,
  fetchComplaintsByUser,
  fetchSupportChats,
  fetchEmailThreadsPage,
  fetchEmailThread,
  fetchFreeTextTranslation,
  markAtomInappropriateContent,
  unmarkAtomInappropriateContent,
  findSimilarMedia,
  fetchVerificationAttempts,
  fetchVerificationMedia,
  getMediaUrl,
} from "@/services/backofficeClient";
import { ROLES } from "@/services/acl";
import UserActivityLog from "@/components/UserActivityLog.vue";

const DETAILS_CHATS_PAGE_SIZE = 25;
const DETAILS_EMAILS_PAGE_SIZE = 25;
const ACTIVE_PLAN_STATUSES = [
  "active",
  "subscription_trialing",
  "subscription_in_grace_period",
];
const COMPLAINT_STATUS_ORDER = ["pending", "resolved", "rejected"];
const TRANSLATION_LANGUAGES = [
  { code: "English", label: "English" },
  { code: "Persian", label: "Persian" },
];

const props = defineProps({
  userId: { type: String, default: null },
  activeChatId: { type: String, default: null },
  fallbackName: { type: String, default: "" },
  emptyMessage: { type: String, default: "No user selected." },
});
const emit = defineEmits(["select-chat"]);

const store = useStore();
const canViewUser = computed(() => store.getters.hasRole(ROLES.USER_INDEX));
const canEditUser = computed(() => store.getters.hasRole(ROLES.USER_EDIT));
const canViewPay = computed(() => store.getters.hasRole(ROLES.PAY_INDEX));
const canViewComplaints = computed(() => store.getters.hasRole(ROLES.COMPLAINT_INDEX));
const canViewEmail = computed(() =>
  store.getters.hasRole(ROLES.EMAIL_MANAGEMENT_INDEX)
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const formatDay = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

const formatDate = (value) => {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  const h = `${d.getHours()}`.padStart(2, "0");
  const m = `${d.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${h}:${m}`;
};

const calculateAge = (birthdate) => {
  if (!birthdate) return null;
  const now = new Date();
  let age = now.getFullYear() - birthdate.getFullYear();
  const monthDiff = now.getMonth() - birthdate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthdate.getDate())) {
    age -= 1;
  }
  return age;
};

const localizedValue = (value) =>
  value && typeof value === "object" ? value.en || value.fa || "" : value || "";

const latestMessagePreview = (chat) => {
  const msg = chat.latestMessage;
  if (!msg) return "—";
  const content = msg.content;
  if (!content) return "—";
  if (content.type === "text") return content.text || "—";
  if (content.type === "media") return "[Media]";
  return "—";
};

const parseChat = (raw) => ({
  id: raw.id,
  userId: raw.userId,
  subject: raw.subject || "—",
  isEscalated: raw.isEscalated || false,
  isClosed: raw.isClosed ?? false,
  assignedAdminId: raw.assignedAdminId || null,
  createdAt: raw.createdAt ? new Date(raw.createdAt) : null,
  lastMessageAt: raw.lastMessageAt ? new Date(raw.lastMessageAt) : null,
  unreadCount: raw.unreadCount || 0,
  user: raw.user || null,
  latestMessage: raw.latestMessage || null,
});

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------
const detailsUser = ref(null);
const detailsLoading = ref(false);
const detailsError = ref("");

const parseDetailsUser = (raw) => {
  const toDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const birthdate = toDate(raw.birthdate);
  return {
    id: raw.id ? String(raw.id) : "",
    name: raw.name || "Unknown",
    previousNames: Array.isArray(raw.previousNames) ? raw.previousNames : [],
    email: raw.email || "",
    phone: raw.phone || "",
    status: raw.status || "",
    gender: raw.gender || "",
    verificationStatus: raw.verificationStatus || "",
    birthdate,
    age: raw.age ?? calculateAge(birthdate),
    previousBirthdates: (Array.isArray(raw.previousBirthdates)
      ? raw.previousBirthdates
      : []
    )
      .map(toDate)
      .filter(Boolean),
    address: raw.address || null,
    atoms: Array.isArray(raw.atoms) ? raw.atoms : [],
    invalidityReasons: Array.isArray(raw.invalidityReasons)
      ? raw.invalidityReasons
      : [],
  };
};

const invalidityReasonsLabel = computed(() => {
  const reasons = detailsUser.value?.invalidityReasons || [];
  if (!reasons.length) return "—";
  return reasons.map((reason) => reason?.type || "unknown").join(", ");
});

const bioAtom = computed(() => {
  const atoms = detailsUser.value?.atoms || [];
  return (
    atoms.find(
      (atom) =>
        atom?.attribute === "bio" &&
        typeof atom.textValue === "string" &&
        atom.textValue.trim()
    ) || null
  );
});

const photoAtoms = computed(() => {
  const atoms = detailsUser.value?.atoms || [];
  return atoms.filter(
    (atom) => atom?.mediaPath && atom.mediaPath.toLowerCase().includes("photo")
  );
});

const voiceAtoms = computed(() => {
  const atoms = detailsUser.value?.atoms || [];
  return atoms.filter(
    (atom) => atom?.mediaPath && atom.mediaPath.toLowerCase().includes("voice")
  );
});

const isAtomFlaggedInappropriate = (atomId) => {
  if (!atomId) return false;
  const reasons = detailsUser.value?.invalidityReasons || [];
  return reasons.some(
    (reason) =>
      reason?.type === "inappropriateContent" &&
      reason?.atomReference?.id === atomId
  );
};

const translateMenuOpen = ref(false);
const translateLoading = ref(false);
const translateError = ref("");
const bioTranslation = ref("");
const inappropriateContentLoadingFor = ref(null);
const inappropriateContentError = ref("");

const toggleTranslateMenu = () => {
  translateMenuOpen.value = !translateMenuOpen.value;
};

const translateBio = async (language) => {
  translateMenuOpen.value = false;
  translateError.value = "";
  if (!bioAtom.value?.textValue) return;
  translateLoading.value = true;
  try {
    const payload = await fetchFreeTextTranslation(bioAtom.value.textValue, language);
    const translations = Array.isArray(payload?.translations) ? payload.translations : [];
    bioTranslation.value = translations[0]?.translatedText || "";
  } catch (err) {
    translateError.value = err?.payload?.message || err?.message || "Failed to translate bio.";
  } finally {
    translateLoading.value = false;
  }
};

const toggleAtomInappropriateContent = async (atom) => {
  if (!atom?.id || !props.userId || inappropriateContentLoadingFor.value) return;
  inappropriateContentLoadingFor.value = atom.id;
  inappropriateContentError.value = "";
  const wasFlagged = isAtomFlaggedInappropriate(atom.id);
  try {
    const payload = wasFlagged
      ? await unmarkAtomInappropriateContent(props.userId, atom.id)
      : await markAtomInappropriateContent(props.userId, atom.id);
    detailsUser.value = parseDetailsUser(payload);
  } catch (err) {
    inappropriateContentError.value =
      err?.payload?.message || err?.message || "Failed to update inappropriate content flag.";
  } finally {
    inappropriateContentLoadingFor.value = null;
  }
};

const detailsLocation = computed(() => {
  const address = detailsUser.value?.address;
  if (!address) return "";
  const parts = [
    localizedValue(address.city),
    localizedValue(address.state),
    localizedValue(address.country),
  ].filter(Boolean);
  return parts.join(", ");
});

const loadDetailsUser = async (userId) => {
  detailsLoading.value = true;
  detailsError.value = "";
  bioTranslation.value = "";
  translateError.value = "";
  translateMenuOpen.value = false;
  inappropriateContentError.value = "";
  try {
    detailsUser.value = parseDetailsUser(await fetchUserById(userId));
  } catch (err) {
    detailsError.value = err?.message || "Failed to load user.";
  } finally {
    detailsLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Sessions (used to derive comma-separated device/session facets)
// ---------------------------------------------------------------------------
const detailsSessions = ref([]);
const detailsSessionsLoading = ref(false);
const detailsSessionsError = ref("");

const loadDetailsSessions = async (userId) => {
  detailsSessionsLoading.value = true;
  detailsSessionsError.value = "";
  try {
    const payload = await fetchUserSessions(userId);
    detailsSessions.value = Array.isArray(payload?.items) ? payload.items : [];
  } catch (err) {
    detailsSessionsError.value = err?.message || "Failed to load sessions.";
  } finally {
    detailsSessionsLoading.value = false;
  }
};

const distinctSessionValues = (key) => {
  const values = new Set();
  detailsSessions.value.forEach((session) => {
    const value = session?.[key];
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v) values.add(String(v));
      });
    } else if (value) {
      values.add(String(value));
    }
  });
  return [...values].sort();
};

const sessionFieldDisplay = (key) => {
  if (detailsSessionsLoading.value) return "Loading…";
  if (detailsSessionsError.value) return "—";
  const values = distinctSessionValues(key);
  return values.length ? values.join(", ") : "—";
};

const sessionAppLanguages = computed(() => sessionFieldDisplay("appLanguage"));
const sessionSimCountries = computed(() => sessionFieldDisplay("simCountries"));
const sessionSystemLocales = computed(() => sessionFieldDisplay("systemLocales"));
const sessionTimezones = computed(() => sessionFieldDisplay("timezone"));
const sessionCountries = computed(() => sessionFieldDisplay("countries"));
const sessionPlatforms = computed(() => sessionFieldDisplay("platform"));

// ---------------------------------------------------------------------------
// Verification photos
// ---------------------------------------------------------------------------
const verificationAttempts = ref([]);
const verificationMediaLoading = ref(false);
const verificationMediaError = ref("");

const parseVerificationAttempt = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    id: raw.id ? String(raw.id) : "",
    verificationMediaPaths: Array.isArray(raw.verificationMediaPaths)
      ? raw.verificationMediaPaths.map((path) => (path == null ? "" : String(path))).filter(Boolean)
      : [],
    verificationPhotoPaths: Array.isArray(raw.verificationPhotoPaths)
      ? raw.verificationPhotoPaths.map((path) => (path == null ? "" : String(path))).filter(Boolean)
      : [],
  };
};

const loadVerificationAttempts = async (userId) => {
  verificationMediaLoading.value = true;
  verificationMediaError.value = "";
  try {
    const payload = await fetchVerificationAttempts(userId);
    const items = Array.isArray(payload) ? payload : [];
    verificationAttempts.value = items.map((item) => parseVerificationAttempt(item)).filter(Boolean);
  } catch (err) {
    verificationMediaError.value = err?.message || "Failed to load verification photos.";
  } finally {
    verificationMediaLoading.value = false;
  }
};

const verificationPhotoItems = computed(() => {
  const items = [];
  verificationAttempts.value.forEach((attempt) => {
    attempt.verificationMediaPaths.forEach((path, index) => {
      if (path.toLowerCase().includes("photo")) {
        items.push({ key: `${attempt.id}-media-${index}`, kind: "path", path });
      }
    });
    attempt.verificationPhotoPaths.forEach((path, index) => {
      items.push({ key: `${attempt.id}-photo-${index}`, kind: "fetch", attemptId: attempt.id, index });
    });
  });
  return items;
});

// ---------------------------------------------------------------------------
// Complaints
// ---------------------------------------------------------------------------
const complaintsAgainstUser = ref([]);
const complaintsFromUser = ref([]);
const detailsComplaintsLoading = ref(false);
const detailsComplaintsError = ref("");

const complaintStatusBadge = (status) => {
  if (status === "pending") return "bg-gradient-warning";
  if (status === "resolved") return "bg-gradient-success";
  if (status === "rejected") return "bg-gradient-secondary";
  return "bg-gradient-dark";
};

const countByStatus = (complaints) => {
  const counts = {};
  complaints.forEach((complaint) => {
    const status = complaint.status || "unknown";
    counts[status] = (counts[status] || 0) + 1;
  });
  const rank = (status) => {
    const index = COMPLAINT_STATUS_ORDER.indexOf(status);
    return index === -1 ? COMPLAINT_STATUS_ORDER.length : index;
  };
  return Object.entries(counts)
    .map(([status, count]) => ({ status, count }))
    .sort((a, b) => rank(a.status) - rank(b.status));
};

const complaintsAgainstCounts = computed(() =>
  countByStatus(complaintsAgainstUser.value)
);
const complaintsFromCounts = computed(() => countByStatus(complaintsFromUser.value));

const loadDetailsComplaints = async (userId) => {
  detailsComplaintsLoading.value = true;
  detailsComplaintsError.value = "";
  try {
    const [against, from] = await Promise.all([
      fetchComplaintsByUser(userId),
      fetchComplaintsByUser(userId, { groupBy: "reporterUserId" }),
    ]);
    complaintsAgainstUser.value = Array.isArray(against) ? against : [];
    complaintsFromUser.value = Array.isArray(from) ? from : [];
  } catch (err) {
    detailsComplaintsError.value = err?.message || "Failed to load complaints.";
  } finally {
    detailsComplaintsLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Active plans
// ---------------------------------------------------------------------------
const detailsPlans = ref([]);
const detailsPlansLoading = ref(false);
const detailsPlansError = ref("");

const isPlanActive = (plan) => {
  if (plan.acquisitionType === "default_plan") return true;
  if (!ACTIVE_PLAN_STATUSES.includes(plan.status)) return false;
  const now = Date.now();
  if (plan.fromDatetime && plan.fromDatetime.getTime() > now) return false;
  if (plan.toDatetime && plan.toDatetime.getTime() <= now) return false;
  return true;
};

const loadDetailsPlans = async (userId) => {
  detailsPlansLoading.value = true;
  detailsPlansError.value = "";
  try {
    const payload = await fetchUserPurchases({ userId, page: 1, pageSize: 50 });
    const toDate = (value) => (value ? new Date(value) : null);
    detailsPlans.value = (payload?.items || [])
      .map((item) => ({
        id: item.id,
        planId: item.planId || "",
        status: item.status || "",
        acquisitionType: item.acquisitionType || "",
        fromDatetime: toDate(item.fromDatetime),
        toDatetime: toDate(item.toDatetime),
      }))
      .filter(isPlanActive);
  } catch (err) {
    detailsPlansError.value = err?.message || "Failed to load plans.";
  } finally {
    detailsPlansLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Wallet
// ---------------------------------------------------------------------------
const detailsWallet = ref(null);
const detailsWalletLoading = ref(false);
const detailsWalletError = ref("");

const loadDetailsWallet = async (userId) => {
  detailsWalletLoading.value = true;
  detailsWalletError.value = "";
  try {
    detailsWallet.value = await fetchUserWallet({ userId });
  } catch (err) {
    detailsWalletError.value = err?.message || "Failed to load wallet.";
  } finally {
    detailsWalletLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Support chats
// ---------------------------------------------------------------------------
const detailsChats = ref([]);
const detailsChatsLoading = ref(false);
const detailsChatsError = ref("");

const loadDetailsChats = async (userId) => {
  detailsChatsLoading.value = true;
  detailsChatsError.value = "";
  try {
    const payload = await fetchSupportChats({
      userId,
      page: 1,
      pageSize: DETAILS_CHATS_PAGE_SIZE,
    });
    detailsChats.value = (payload?.items || []).map(parseChat);
  } catch (err) {
    detailsChatsError.value = err?.message || "Failed to load chats.";
  } finally {
    detailsChatsLoading.value = false;
  }
};

const findChat = (chatId) => detailsChats.value.find((c) => c.id === chatId) || null;

const patchChat = (chatId, patch) => {
  const idx = detailsChats.value.findIndex((c) => c.id === chatId);
  if (idx !== -1) {
    detailsChats.value[idx] = { ...detailsChats.value[idx], ...patch };
  }
};

const prependChat = (chat) => {
  detailsChats.value = [chat, ...detailsChats.value];
};

// ---------------------------------------------------------------------------
// Emails
// ---------------------------------------------------------------------------
const detailsEmails = ref([]);
const detailsEmailsLoading = ref(false);
const detailsEmailsError = ref("");

const loadDetailsEmails = async (participantEmail) => {
  detailsEmailsLoading.value = true;
  detailsEmailsError.value = "";
  try {
    const payload = await fetchEmailThreadsPage({
      page: 1,
      pageSize: DETAILS_EMAILS_PAGE_SIZE,
      participantEmail,
      deleted: "any",
    });
    detailsEmails.value = (payload?.items || []).map((raw) => ({
      id: raw.id,
      subject: raw.subject || "(no subject)",
      messageCount: raw.messageCount || 0,
      isDeleted: raw.isDeleted || false,
      lastMessageAt: raw.lastMessageAt ? new Date(raw.lastMessageAt) : null,
    }));
  } catch (err) {
    detailsEmailsError.value = err?.message || "Failed to load emails.";
  } finally {
    detailsEmailsLoading.value = false;
  }
};

// ---------------------------------------------------------------------------
// Email reader modal
// ---------------------------------------------------------------------------
const emailModalOpen = ref(false);
const emailThread = ref(null);
const emailMessages = ref([]);
const emailThreadLoading = ref(false);
const emailThreadError = ref("");

const htmlToText = (html) => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return (doc.body?.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
  } catch (err) {
    return html;
  }
};

const emailMessageBody = (message) => {
  if (message.textPlain && message.textPlain.trim()) return message.textPlain;
  if (message.textHtml) return htmlToText(message.textHtml);
  return "";
};

const emailMessageSender = (message) => {
  if (message.direction === "outbound") {
    return message.sentByAdminName || message.from?.email || "You";
  }
  const address = message.from;
  if (!address) return "Unknown sender";
  return address.name ? `${address.name} <${address.email}>` : address.email;
};

const openEmailThread = async (threadId) => {
  emailModalOpen.value = true;
  emailThread.value = null;
  emailMessages.value = [];
  emailThreadError.value = "";
  emailThreadLoading.value = true;
  try {
    const payload = await fetchEmailThread(threadId);
    emailThread.value = payload?.thread || null;
    emailMessages.value = payload?.messages || [];
  } catch (err) {
    emailThreadError.value = err?.message || "Failed to load email.";
  } finally {
    emailThreadLoading.value = false;
  }
};

const closeEmailThread = () => {
  emailModalOpen.value = false;
  emailThread.value = null;
  emailMessages.value = [];
  emailThreadError.value = "";
};

// ---------------------------------------------------------------------------
// Media viewer
// ---------------------------------------------------------------------------
const mediaViewerUrl = ref("");
const mediaViewerObjectUrl = ref("");

const openMedia = (mediaPath) => {
  const url = getMediaUrl(mediaPath);
  if (url) mediaViewerUrl.value = url;
};

const closeMedia = () => {
  mediaViewerUrl.value = "";
  if (mediaViewerObjectUrl.value) {
    URL.revokeObjectURL(mediaViewerObjectUrl.value);
    mediaViewerObjectUrl.value = "";
  }
};

const openVerificationPhoto = async (attemptId, index) => {
  if (!attemptId || typeof index !== "number") return;
  try {
    const payload = await fetchVerificationMedia(attemptId, index);
    if (!(payload?.blob instanceof Blob)) {
      verificationMediaError.value = "Unable to load verification photo.";
      return;
    }
    if (mediaViewerObjectUrl.value) {
      URL.revokeObjectURL(mediaViewerObjectUrl.value);
    }
    const objectUrl = URL.createObjectURL(payload.blob);
    mediaViewerObjectUrl.value = objectUrl;
    mediaViewerUrl.value = objectUrl;
  } catch (err) {
    verificationMediaError.value = err?.message || "Failed to load verification photo.";
  }
};

// ---------------------------------------------------------------------------
// Similar photos
// ---------------------------------------------------------------------------
const similarPhotosModalOpen = ref(false);
const similarPhotosLoading = ref(false);
const similarPhotosError = ref("");
const similarPhotosResults = ref([]);
const similarPhotosSourcePath = ref("");

const findSimilarPhotos = async (mediaPath) => {
  if (!mediaPath || similarPhotosLoading.value) return;
  similarPhotosModalOpen.value = true;
  similarPhotosLoading.value = true;
  similarPhotosError.value = "";
  similarPhotosResults.value = [];
  similarPhotosSourcePath.value = mediaPath;
  try {
    const payload = await findSimilarMedia(mediaPath);
    similarPhotosResults.value = Array.isArray(payload?.items) ? payload.items : [];
  } catch (err) {
    similarPhotosError.value =
      err?.payload?.message || err?.message || "Failed to find similar photos.";
  } finally {
    similarPhotosLoading.value = false;
  }
};

const closeSimilarPhotosModal = () => {
  similarPhotosModalOpen.value = false;
  similarPhotosResults.value = [];
  similarPhotosError.value = "";
  similarPhotosSourcePath.value = "";
};

const formatSimilarityScore = (score) => {
  const value = Number(score);
  if (!Number.isFinite(value)) return "--";
  return `${(value * 100).toFixed(1)}%`;
};

// ---------------------------------------------------------------------------
// Load / refresh
// ---------------------------------------------------------------------------
const activityLogRef = ref(null);

const loadAll = async (userId) => {
  detailsUser.value = null;
  detailsError.value = "";
  detailsPlans.value = [];
  detailsPlansError.value = "";
  detailsWallet.value = null;
  detailsWalletError.value = "";
  detailsChats.value = [];
  detailsChatsError.value = "";
  detailsEmails.value = [];
  detailsEmailsError.value = "";
  detailsSessions.value = [];
  detailsSessionsError.value = "";
  verificationAttempts.value = [];
  verificationMediaError.value = "";
  complaintsAgainstUser.value = [];
  complaintsFromUser.value = [];
  detailsComplaintsError.value = "";
  closeSimilarPhotosModal();
  if (!userId) return;

  loadDetailsChats(userId);
  if (canViewPay.value) {
    loadDetailsPlans(userId);
    loadDetailsWallet(userId);
  }
  if (canViewComplaints.value) {
    loadDetailsComplaints(userId);
  }
  if (!canViewUser.value) return;

  loadDetailsSessions(userId);
  loadVerificationAttempts(userId);
  await loadDetailsUser(userId);
  const email = detailsUser.value?.email;
  if (canViewEmail.value && email && props.userId === userId) {
    loadDetailsEmails(email);
  }
};

watch(
  () => props.userId,
  (userId) => loadAll(userId),
  { immediate: true }
);

const refresh = () => {
  loadAll(props.userId);
  activityLogRef.value?.reload();
};

defineExpose({ refresh, patchChat, prependChat, findChat });
</script>

<template>
  <div class="card">
    <div v-if="!userId"
      class="card-body d-flex flex-column align-items-center justify-content-center text-center text-secondary text-sm empty-panel">
      <i class="ni ni-single-02 d-block mb-3" style="font-size:2.5rem;opacity:.3"></i>
      {{ emptyMessage }}
    </div>

    <div v-else class="card-body p-3 details-panel">
      <!-- Identity header -->
      <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
        <div class="overflow-hidden">
          <a :href="`/users/${userId}`" target="_blank" class="">
            <div class="text-sm fw-semibold text-truncate">
              <i class="fa fa-sm fa-chevron-right"></i>
              {{ detailsUser?.name || fallbackName || "—" }}
            </div>
          </a>
          <div class="text-xxs text-secondary text-truncate">{{ userId }}</div>
        </div>
        <button type="button" class="btn btn-sm-icon-only btn-sm btn-outline-secondary mb-0 flex-shrink-0"
          :disabled="detailsLoading" title="Reload user details" @click="refresh">
          <span v-if="detailsLoading" class="spinner-border spinner-border-sm"></span>
          <i v-else class="fas fa-sync"></i>
        </button>
      </div>

      <!-- Links -->
      <div class="d-flex flex-wrap gap-1 mb-3">
      </div>

      <!-- Profile fields -->
      <div v-if="!canViewUser" class="text-xs text-secondary mb-3">
        You don't have permission to view the profile.
      </div>
      <div v-else-if="detailsError" class="alert alert-danger text-xs py-2 mb-3">{{ detailsError }}</div>
      <div v-else-if="detailsLoading && !detailsUser" class="text-xs text-secondary mb-3">Loading…</div>
      <dl v-else-if="detailsUser" class="details-list mb-3">
        <div v-if="detailsUser.previousNames.length">
          <dt>Name</dt>
          <dd>
            {{ detailsUser.name || "—" }}
            <div v-if="detailsUser.previousNames.length" class="text-xxs text-secondary mt-1">
              was: {{ detailsUser.previousNames.join(", ") }}
            </div>
          </dd>
        </div>
        <div>
          <dt>Birthdate</dt>
          <dd>
            <span v-if="detailsUser.birthdate">
              {{ formatDay(detailsUser.birthdate) }}
              <span class="text-secondary">({{ detailsUser.age }})</span>
            </span>
            <span v-else>—</span>
            <div v-if="detailsUser.previousBirthdates.length" class="text-xxs text-secondary mt-1">
              was: {{ detailsUser.previousBirthdates.map(formatDay).join(", ") }}
            </div>
          </dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            <span class="badge badge-sm"
              :class="detailsUser.status === 'active' ? 'bg-gradient-success' : 'bg-gradient-secondary'">
              {{ detailsUser.status || "—" }}
            </span>
          </dd>
        </div>
        <div v-if="detailsUser.gender">
          <dt>Gender</dt>
          <dd>{{ detailsUser.gender || "—" }}</dd>
        </div>
        <div v-if="detailsLocation">
          <dt>Location</dt>
          <dd>{{ detailsLocation || "—" }}</dd>
        </div>
        <div v-if="detailsUser.verificationStatus">
          <dt>Verified</dt>
          <dd>
            <span class="badge badge-sm"
              :class="detailsUser.verificationStatus === 'verified' ? 'bg-gradient-success' : 'bg-gradient-secondary'">
              {{ detailsUser.verificationStatus || "not verified" }}
            </span>
          </dd>
        </div>
        <div v-if="bioAtom">
          <dt>Bio</dt>
          <dd>
            <div class="d-flex align-items-start justify-content-between gap-1">
              <span class="flex-grow-1">{{ bioAtom.textValue }}</span>
              <div class="d-flex gap-1 flex-shrink-0">
                <button v-if="canEditUser" type="button" class="btn btn-sm-icon-only btn-xs mb-0"
                  :class="isAtomFlaggedInappropriate(bioAtom.id) ? 'btn-danger' : 'btn-outline-danger'"
                  :disabled="inappropriateContentLoadingFor === bioAtom.id"
                  :title="isAtomFlaggedInappropriate(bioAtom.id) ? 'Unmark inappropriate' : 'Mark inappropriate'"
                  @click="toggleAtomInappropriateContent(bioAtom)">
                  <span v-if="inappropriateContentLoadingFor === bioAtom.id"
                    class="spinner-border spinner-border-sm"></span>
                  <span v-else>{{ isAtomFlaggedInappropriate(bioAtom.id) ? "🚫" : "🚩" }}</span>
                </button>
                <div class="position-relative">
                  <button type="button" class="btn btn-sm-icon-only btn-xs btn-outline-secondary mb-0"
                    :disabled="translateLoading" title="Translate" @click="toggleTranslateMenu">
                    <span v-if="translateLoading" class="spinner-border spinner-border-sm"></span>
                    <span v-else>🌐</span>
                  </button>
                  <div v-if="translateMenuOpen" class="position-absolute bg-white shadow-sm rounded-2 border py-1"
                    style="top: 100%; right: 0; z-index: 20; min-width: 130px;">
                    <button v-for="language in TRANSLATION_LANGUAGES" :key="language.code" type="button"
                      class="dropdown-item text-xs py-1 px-2" @click="translateBio(language.code)">
                      {{ language.label }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="translateError" class="text-danger text-xxs mt-1">{{ translateError }}</div>
            <div v-if="inappropriateContentError" class="text-danger text-xxs mt-1">
              {{ inappropriateContentError }}
            </div>
            <div v-if="bioTranslation" class="mt-1 pt-1 text-xxs" style="border-top: 1px solid rgba(0, 0, 0, 0.08);">
              <span class="fw-semibold text-secondary">Translation: </span>{{ bioTranslation }}
            </div>
          </dd>
        </div>
        <div v-if="sessionAppLanguages">
          <dt>Languages</dt>
          <dd>{{ sessionAppLanguages }}</dd>
        </div>
        <div v-if="sessionSimCountries">
          <dt>Sim cards</dt>
          <dd>{{ sessionSimCountries }}</dd>
        </div>
        <div v-if="sessionSystemLocales">
          <dt>Locales</dt>
          <dd>{{ sessionSystemLocales }}</dd>
        </div>
        <div v-if="sessionTimezones">
          <dt>Timezones</dt>
          <dd>{{ sessionTimezones }}</dd>
        </div>
        <div v-if="sessionCountries">
          <dt>Countries</dt>
          <dd>{{ sessionCountries }}</dd>
        </div>
        <div v-if="sessionPlatforms">
          <dt>Platforms</dt>
          <dd>{{ sessionPlatforms }}</dd>
        </div>
        <div v-if="invalidityReasonsLabel !== '—'">
          <dt>Invalidity</dt>
          <dd>{{ invalidityReasonsLabel }}</dd>
        </div>
        <div v-if="photoAtoms.length">
          <dt>Photos</dt>
          <dd>
            <div class="details-photos">
              <div v-for="photo in photoAtoms" :key="photo.id || photo.mediaPath" class="details-photo">
                <div class="details-atom-attribute text-truncate" :title="photo.attribute">
                  {{ photo.attribute || "—" }}
                </div>
                <img :src="getMediaUrl(photo.mediaPath)" alt="profile photo" title="Open full size"
                  @click="openMedia(photo.mediaPath)" />
                <div class="d-flex gap-1 mt-1">
                  <button v-if="canEditUser" type="button" class="btn btn-sm-icon-only btn-xs mb-0"
                    :class="isAtomFlaggedInappropriate(photo.id) ? 'btn-danger' : 'btn-outline-danger'"
                    :disabled="inappropriateContentLoadingFor === photo.id"
                    :title="isAtomFlaggedInappropriate(photo.id) ? 'Unmark inappropriate' : 'Mark inappropriate'"
                    @click="toggleAtomInappropriateContent(photo)">
                    <span v-if="inappropriateContentLoadingFor === photo.id"
                      class="spinner-border spinner-border-sm"></span>
                    <span v-else>{{ isAtomFlaggedInappropriate(photo.id) ? "🚫" : "🚩" }}</span>
                  </button>
                  <button type="button" class="btn btn-sm-icon-only btn-xs btn-outline-secondary mb-0"
                    :disabled="similarPhotosLoading && similarPhotosSourcePath === photo.mediaPath"
                    title="Find similar photos" @click="findSimilarPhotos(photo.mediaPath)">
                    <span v-if="similarPhotosLoading && similarPhotosSourcePath === photo.mediaPath"
                      class="spinner-border spinner-border-sm"></span>
                    <span v-else>🔍</span>
                  </button>
                </div>
              </div>
            </div>
          </dd>
        </div>
        <div v-if="verificationMediaLoading || verificationMediaError || verificationPhotoItems.length">
          <dt>Verifications</dt>
          <dd>
            <div v-if="verificationMediaLoading" class="text-xxs text-secondary">Loading…</div>
            <div v-else-if="verificationMediaError" class="text-danger text-xxs">{{ verificationMediaError }}</div>
            <div v-else-if="verificationPhotoItems.length === 0" class="text-xxs text-secondary">—</div>
            <div v-else class="details-photos">
              <template v-for="item in verificationPhotoItems" :key="item.key">
                <div v-if="item.kind === 'path'" class="details-photo">
                  <img :src="getMediaUrl(item.path)" alt="verification photo" title="Open full size"
                    @click="openMedia(item.path)" />
                </div>
                <button v-else type="button" class="details-photo details-photo-fetch" title="Load verification photo"
                  @click="openVerificationPhoto(item.attemptId, item.index)">
                  <i class="fas fa-image"></i>
                </button>
              </template>
            </div>
          </dd>
        </div>
        <div v-if="voiceAtoms.length">
          <dt>Voice</dt>
          <dd>
            <div class="d-flex flex-column gap-2">
              <div v-for="voice in voiceAtoms" :key="voice.id || voice.mediaPath">
                <div class="details-atom-attribute text-center mb-0">{{ voice.attribute || "—" }}</div>
                <div class="d-flex align-items-center gap-2">
                  <audio :src="getMediaUrl(voice.mediaPath)" controls style="height: 32px;width: 100%;"></audio>
                  <button v-if="canEditUser" type="button" class="btn btn-sm-icon-only btn-xs mb-0 flex-shrink-0"
                    :class="isAtomFlaggedInappropriate(voice.id) ? 'btn-danger' : 'btn-outline-danger'"
                    :disabled="inappropriateContentLoadingFor === voice.id"
                    :title="isAtomFlaggedInappropriate(voice.id) ? 'Unmark inappropriate' : 'Mark inappropriate'"
                    @click="toggleAtomInappropriateContent(voice)">
                    <span v-if="inappropriateContentLoadingFor === voice.id"
                      class="spinner-border spinner-border-sm"></span>
                    <span v-else>{{ isAtomFlaggedInappropriate(voice.id) ? "🚫" : "🚩" }}</span>
                  </button>
                </div>
              </div>
            </div>
          </dd>
        </div>
      </dl>

      <!-- Complaints -->
      <div v-if="canViewComplaints" class="details-section">
        <div class="details-section-title">
          <a v-if="canViewComplaints" :href="`/complaints/reported/${userId}`" target="_blank" class="mb-0"
            title="Complaints against this user">
            <i class="fa fa-chevron-right"></i>
            Complaints
          </a>
        </div>
        <div v-if="detailsComplaintsError" class="text-danger text-xs">{{ detailsComplaintsError }}</div>
        <div v-else-if="detailsComplaintsLoading" class="text-xs text-secondary">Loading…</div>
        <template v-else>
          <div class="d-flex align-items-center justify-content-between gap-2">
            <span class="text-xs">Against user</span>
            <span class="text-xs fw-semibold">{{ complaintsAgainstUser.length }}</span>
          </div>
          <div v-if="complaintsAgainstCounts.length" class="d-flex flex-wrap gap-1 mt-1 mb-2">
            <span v-for="entry in complaintsAgainstCounts" :key="`against-${entry.status}`" class="badge badge-sm"
              :class="complaintStatusBadge(entry.status)">
              {{ entry.status }} {{ entry.count }}
            </span>
          </div>

          <div class="d-flex align-items-center justify-content-between gap-2 mt-2">
            <span class="text-xs">From user</span>
            <span class="text-xs fw-semibold">{{ complaintsFromUser.length }}</span>
          </div>
          <div v-if="complaintsFromCounts.length" class="d-flex flex-wrap gap-1 mt-1 mb-2">
            <span v-for="entry in complaintsFromCounts" :key="`from-${entry.status}`" class="badge badge-sm"
              :class="complaintStatusBadge(entry.status)">
              {{ entry.status }} {{ entry.count }}
            </span>
          </div>
        </template>
      </div>

      <!-- Moderation history -->
      <div v-if="canViewUser" class="details-section">
        <UserActivityLog ref="activityLogRef" :user-id="userId" compact title="Moderation history" />
      </div>

      <!-- Active plans -->
      <div v-if="canViewPay" class="details-section">
        <div class="details-section-title">
          <a v-if="canViewComplaints" :href="`/users/${userId}?tab=purchases`" target="_blank" class="mb-0"
            title="Complaints against this user">
            <i class="fa fa-chevron-right"></i>
            Active plans
          </a>
        </div>
        <div v-if="detailsPlansError" class="text-danger text-xs">{{ detailsPlansError }}</div>
        <div v-else-if="detailsPlansLoading" class="text-xs text-secondary">Loading…</div>
        <div v-else-if="!detailsPlans.length" class="text-xs text-secondary">No active plan.</div>
        <div v-else v-for="plan in detailsPlans" :key="plan.id" class="border rounded-2 px-2 py-1 mb-1">
          <div class="d-flex align-items-center justify-content-between gap-2">
            <span class="text-xs fw-semibold text-truncate">{{ plan.planId || "—" }}</span>
            <span class="badge badge-sm bg-gradient-success flex-shrink-0">{{ plan.status }}</span>
          </div>
          <div class="text-xxs text-secondary">
            {{ plan.fromDatetime ? formatDay(plan.fromDatetime) : "—" }} →
            {{ plan.toDatetime ? formatDay(plan.toDatetime) : "∞" }}
          </div>
        </div>
      </div>

      <!-- Wallet -->
      <div v-if="canViewPay" class="details-section">
        <div class="details-section-title">
          <a v-if="canViewComplaints" :href="`/users/${userId}?tab=purchases`" target="_blank" class="mb-0"
            title="Complaints against this user">
            <i class="fa fa-chevron-right"></i>
            Wallet balance
          </a>
        </div>
        <div v-if="detailsWalletError" class="text-danger text-xs">{{ detailsWalletError }}</div>
        <div v-else-if="detailsWalletLoading" class="text-xs text-secondary">Loading…</div>
        <div v-else-if="detailsWallet" class="text-sm fw-semibold">
          {{ detailsWallet.balance?.amount?.toFixed(2) ?? "—" }}
          <span class="text-xs text-secondary fw-normal">{{ detailsWallet.balance?.currencyCode }}</span>
        </div>
        <div v-else class="text-xs text-secondary">—</div>
      </div>

      <!-- Support chats -->
      <div class="details-section">
        <div class="details-section-title">
          Support chats
          <span class="text-secondary fw-normal">({{ detailsChats.length }})</span>
        </div>
        <div v-if="detailsChatsError" class="text-danger text-xs">{{ detailsChatsError }}</div>
        <div v-else-if="detailsChatsLoading" class="text-xs text-secondary">Loading…</div>
        <div v-else-if="!detailsChats.length" class="text-xs text-secondary">No chats.</div>
        <button v-else v-for="chat in detailsChats" :key="chat.id" class="details-row"
          :class="{ 'details-row-active': chat.id === activeChatId }" @click="emit('select-chat', chat.id)">
          <div class="d-flex align-items-center justify-content-between gap-2">
            <span class="text-xs fw-semibold text-truncate">{{ chat.subject }}</span>
            <span class="text-xxs text-secondary flex-shrink-0">{{ formatDay(chat.lastMessageAt) }}</span>
          </div>
          <div class="d-flex align-items-center gap-1 mt-1">
            <span v-if="chat.isClosed" class="badge badge-sm bg-gradient-secondary">Closed</span>
            <span v-if="chat.isEscalated" class="badge badge-sm bg-gradient-danger">Escalated</span>
            <span v-if="chat.unreadCount > 0" class="badge badge-sm bg-gradient-info">
              {{ chat.unreadCount }} unread
            </span>
            <span class="text-xxs text-muted text-truncate">{{ latestMessagePreview(chat) }}</span>
          </div>
        </button>
      </div>

      <!-- Emails -->
      <div v-if="canViewEmail" class="details-section">
        <div class="details-section-title">
          Emails
          <span class="text-secondary fw-normal">({{ detailsEmails.length }})</span>
        </div>
        <div v-if="!canViewUser" class="text-xs text-secondary">
          Requires permission to read the user's email address.
        </div>
        <div v-else-if="detailsEmailsError" class="text-danger text-xs">{{ detailsEmailsError }}</div>
        <div v-else-if="detailsEmailsLoading || detailsLoading" class="text-xs text-secondary">Loading…</div>
        <div v-else-if="!detailsUser?.email" class="text-xs text-secondary">No email address.</div>
        <div v-else-if="!detailsEmails.length" class="text-xs text-secondary">No emails.</div>
        <button v-else v-for="thread in detailsEmails" :key="thread.id" class="details-row"
          @click="openEmailThread(thread.id)">
          <div class="d-flex align-items-center justify-content-between gap-2">
            <span class="text-xs fw-semibold text-truncate">{{ thread.subject }}</span>
            <span class="text-xxs text-secondary flex-shrink-0">{{ formatDay(thread.lastMessageAt) }}</span>
          </div>
          <div class="d-flex align-items-center gap-1 mt-1">
            <span v-if="thread.isDeleted" class="badge badge-sm bg-gradient-dark">Deleted</span>
            <span class="text-xxs text-muted">{{ thread.messageCount }} messages</span>
          </div>
        </button>
      </div>
    </div>
  </div>

  <!-- ===== Email Reader Modal ===== -->
  <Teleport to="body">
    <div v-if="emailModalOpen" class="email-modal-backdrop" @click.self="closeEmailThread">
      <div class="email-modal-dialog card shadow-lg">
        <div class="card-header pb-2 d-flex align-items-start justify-content-between gap-2">
          <div class="overflow-hidden">
            <h6 class="mb-0 text-truncate">{{ emailThread?.subject || "Email" }}</h6>
            <span class="text-xs text-secondary">{{ emailThread?.participant?.email }}</span>
          </div>
          <button class="btn btn-sm btn-outline-secondary mb-0 flex-shrink-0" @click="closeEmailThread">✕</button>
        </div>

        <div class="card-body">
          <div v-if="emailThreadLoading" class="text-secondary text-sm">Loading…</div>
          <div v-else-if="emailThreadError" class="alert alert-danger text-sm py-2 mb-0">{{ emailThreadError }}</div>
          <div v-else-if="!emailMessages.length" class="text-secondary text-sm">No messages.</div>
          <div v-else v-for="message in emailMessages" :key="message.id" class="mb-3 d-flex"
            :class="message.direction === 'outbound' ? 'justify-content-end' : 'justify-content-start'">
            <div class="px-3 py-2 rounded-3 text-sm" style="max-width:85%"
              :class="message.direction === 'outbound' ? 'bg-gradient-primary text-white' : 'bg-gray-100'">
              <div class="d-flex align-items-center justify-content-between gap-3 mb-1" style="font-size:11px"
                :class="message.direction === 'outbound' ? 'text-white-50' : 'text-secondary'">
                <span class="fw-semibold">{{ emailMessageSender(message) }}</span>
                <span class="flex-shrink-0">{{ formatDate(message.sentAt) }}</span>
              </div>
              <div style="white-space:pre-wrap">{{ emailMessageBody(message) }}</div>
            </div>
          </div>
        </div>

        <div class="card-footer pt-2 pb-3 text-end">
          <a :href="`/email?threadId=${emailThread?.id}`" target="_blank" class="btn btn-sm btn-outline-primary mb-0">
            Open in Email Inbox
          </a>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ===== Media Viewer ===== -->
  <Teleport to="body">
    <div v-if="mediaViewerUrl" class="media-viewer-backdrop" @click.self="closeMedia">
      <img :src="mediaViewerUrl" alt="media preview" class="media-viewer-image" />
      <button type="button" class="btn btn-light btn-sm mb-0 media-viewer-close" @click="closeMedia">
        Close
      </button>
    </div>
  </Teleport>

  <!-- ===== Similar Photos Modal ===== -->
  <Teleport to="body">
    <div v-if="similarPhotosModalOpen" class="similar-photos-modal-backdrop" @click.self="closeSimilarPhotosModal">
      <div class="similar-photos-modal-dialog card shadow-lg">
        <div class="card-header pb-2 d-flex align-items-center justify-content-between">
          <h6 class="mb-0">Similar photos</h6>
          <button class="btn btn-sm btn-outline-secondary mb-0" @click="closeSimilarPhotosModal">✕</button>
        </div>
        <div class="card-body">
          <div v-if="similarPhotosLoading" class="text-secondary text-sm">
            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
            Searching for similar photos...
          </div>
          <div v-else-if="similarPhotosError" class="alert alert-danger text-sm py-2 mb-0">
            {{ similarPhotosError }}
          </div>
          <div v-else-if="similarPhotosResults.length === 0" class="text-secondary text-sm">
            No highly similar photos found on other users.
          </div>
          <div v-else class="d-flex flex-wrap gap-3">
            <div v-for="item in similarPhotosResults" :key="item.path" class="text-center" style="width: 110px">
              <img v-if="getMediaUrl(item.path)" :src="getMediaUrl(item.path)" alt="similar media" class="rounded"
                style="width: 110px; height: 110px; object-fit: cover; cursor: zoom-in" @click="openMedia(item.path)" />
              <p class="text-xs mb-0 mt-1">
                <a v-if="item.user?.id" :href="`/users/${item.user.id}`" target="_blank">
                  {{ item.user?.name || item.user.id }}
                </a>
                <span v-else>Unknown user</span>
              </p>
              <p class="text-xxs text-secondary mb-0">{{ item.user?.status || "--" }}</p>
              <p class="text-xxs text-secondary mb-0">
                Similarity: {{ formatSimilarityScore(item.similarityScore) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.bg-gray-100 {
  background-color: #f0f2f5;
}

.empty-panel {
  min-height: 320px;
}

.details-panel {
  overflow-y: auto;
}

.details-list {
  margin: 0;
}

.details-list>div {
  display: grid;
  grid-template-columns: 74px minmax(0, 1fr);
  gap: 0.5rem;
  padding: 0.4rem 0;
  border-top: 1px solid #f0f2f5;
}

.details-list dt {
  font-size: 11px;
  font-weight: 600;
  color: #8392ab;
}

.details-list dd {
  margin: 0;
  font-size: 12px;
  word-break: break-word;
}

.details-section {
  padding-top: 0.75rem;
  margin-top: 0.75rem;
  border-top: 1px solid #f0f2f5;
}

.details-section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #8392ab;
  margin-bottom: 0.5rem;
}

.details-row {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.4rem 0.5rem;
  margin-bottom: 0.25rem;
  overflow: hidden;
}

.details-row:hover {
  background-color: #f8f9fa;
}

.details-row-active {
  border-color: #5e72e4;
  background-color: #eef1fd;
}

.details-photos {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  overflow-x: auto;
  padding-bottom: 0.25rem;
}

.details-photo {
  flex-shrink: 0;
  width: 64px;
}

.details-photo img {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 0.375rem;
  cursor: zoom-in;
}

.details-photo-fetch {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px dashed #cbd3da;
  border-radius: 0.375rem;
  color: #8392ab;
  padding: 0;
}

.details-photo-fetch:hover {
  background-color: #f8f9fa;
}

.details-atom-attribute {
  font-size: 10px;
  color: #8392ab;
  margin-top: 0.15rem;
  text-align: center;
}

.email-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.email-modal-dialog {
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  overflow-y: auto;
}

.similar-photos-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1055;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.similar-photos-modal-dialog {
  width: 100%;
  max-width: 640px;
  max-height: 85vh;
  overflow-y: auto;
}

.media-viewer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem;
}

.media-viewer-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 0.5rem;
}

.media-viewer-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
}
</style>
